import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, ChevronDown, ChevronUp, ArrowUpRight, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { useUser } from '../context/UserContext';
import { fmtCurrency } from '../utils';
import T from './crmTheme';
function InboxView({ onPromoted }) {
  const { user } = useUser();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [promoting, setPromoting] = useState(null);
  const [discarding, setDiscarding] = useState(null);

  const canPromote = ['bd','manager','director'].includes(user?.role);
  const [promoteModal, setPromoteModal] = useState(null);
  const [promoteForm, setPromoteForm] = useState({ source: '', opening_notes: '', estimated_value: '' });
  const [promoteError, setPromoteError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', opening_notes: '', contact_id: '' });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (item) => {
    setEditForm({ title: item.title || '', opening_notes: item.opening_notes || '', contact_id: item.contact?.id || '' });
    setEditError('');
    setEditItem(item);
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) { setEditError('Project name is required'); return; }
    setEditSaving(true);
    try {
      const { api } = await import('../api');
      await api.crmUpdateLead(editItem.id, {
        title: editForm.title.trim(),
        opening_notes: editForm.opening_notes.trim() || null,
      });
      setEditItem(null);
      setSuccessMsg('Updated successfully');
      setTimeout(() => setSuccessMsg(''), 3000);
      await load();
    } catch(e) { setEditError(e.message || 'Failed to save'); }
    setEditSaving(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { api } = await import('../api');
      const data = await api.crmInbox();
      setItems(data);
    } catch(e) { setError(e.message || 'Failed to load inbox'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const promote = (item) => {
    setPromoteForm({ source: item.source || 'inbound', opening_notes: '', estimated_value: '' });
    setPromoteError('');
    setPromoteModal(item);
  };

  const submitPromote = async () => {
    if (!promoteModal) return;
    if (!promoteForm.opening_notes.trim()) { setPromoteError('Opening notes are required'); return; }
    setPromoting(promoteModal.id);
    try {
      const { api } = await import('../api');
      const res = await api.crmPromoteInbox(promoteModal.id, {
        source: promoteForm.source,
        opening_notes: promoteForm.opening_notes.trim(),
        estimated_value_pence: promoteForm.estimated_value ? Math.round(parseFloat(promoteForm.estimated_value) * 100) : null,
      });
      setPromoteModal(null);
      setSuccessMsg(res.message || 'Promoted successfully');
      setTimeout(() => setSuccessMsg(''), 4000);
      await load();
      if (onPromoted) onPromoted();
    } catch(e) { setPromoteError(e.message || 'Failed to promote'); }
    setPromoting(null);
  };

  const discard = async (id) => {
    setDiscarding(id);
    try {
      const { api } = await import('../api');
      await api.crmDiscardInbox(id);
      await load();
    } catch(e) { setError(e.message || 'Failed to discard'); }
    setDiscarding(null);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading inbox…</div>;
  if (error) return <div style={{ padding: 16, background: '#7f1d1d', color: '#fca5a5', borderRadius: 8, fontSize: 13 }}>{error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: T.text }}>
            Opportunity <span style={{ color: T.teal }}>Inbox</span>
          </h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: T.textMuted }}>
            Unqualified inbound opportunities logged by Feeder. Promote to pipeline or discard.
          </p>
        </div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 20, fontWeight: 700, color: T.teal }}>{items.length}</div>
      </div>

      {successMsg && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, fontSize: 12, color: '#10b981', fontWeight: 500 }}>
          ✓ {successMsg}
        </div>
      )}

      {editItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600 }}>Edit Inbox Item</h3>
            <p style={{ margin: '0 0 20px', fontSize: 12, color: '#64748b' }}>{editItem.company?.name || 'Unknown Company'}</p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', display: 'block', marginBottom: 5 }}>Project Name *</label>
              <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} autoFocus />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', display: 'block', marginBottom: 5 }}>Notes</label>
              <textarea value={editForm.opening_notes} onChange={e => setEditForm(f => ({ ...f, opening_notes: e.target.value }))}
                rows={4} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>

            {editError && <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, fontSize: 12, color: '#dc2626' }}>{editError}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditItem(null)}
                style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#64748b' }}>
                Cancel
              </button>
              <button onClick={saveEdit} disabled={editSaving}
                style={{ padding: '8px 18px', background: '#0e9aaa', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {promoteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600 }}>Promote to Pipeline</h3>
            <p style={{ margin: '0 0 20px', fontSize: 12, color: '#64748b' }}>{promoteModal.title}</p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', display: 'block', marginBottom: 5 }}>Source *</label>
              <select value={promoteForm.source} onChange={e => setPromoteForm(f => ({ ...f, source: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }}>
                {[['inbound','Inbound'],['referral','Referral'],['framework','Framework'],['repeat_client','Repeat Client'],['cold_outreach','Cold Outreach'],['event','Event / Exhibition']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', display: 'block', marginBottom: 5 }}>Opening Notes *</label>
              <textarea value={promoteForm.opening_notes} onChange={e => setPromoteForm(f => ({ ...f, opening_notes: e.target.value }))}
                placeholder="Summarise the opportunity and any initial qualification notes…"
                rows={4} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', display: 'block', marginBottom: 5 }}>Estimated Value (£) <span style={{ fontWeight: 400, textTransform: 'none' }}>optional</span></label>
              <input type="number" value={promoteForm.estimated_value} onChange={e => setPromoteForm(f => ({ ...f, estimated_value: e.target.value }))}
                placeholder="e.g. 85000" style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
            </div>
            {promoteError && <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, fontSize: 12, color: '#dc2626' }}>{promoteError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setPromoteModal(null); setPromoteError(''); }}
                style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#64748b' }}>
                Cancel
              </button>
              <button onClick={submitPromote} disabled={!!promoting}
                style={{ padding: '8px 18px', background: '#10b981', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                {promoting ? 'Promoting…' : '↑ Promote to New'}
              </button>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: T.textMuted, fontSize: 13, border: `1px dashed ${T.border}`, borderRadius: 10 }}>
          📥 Inbox is empty
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(item => (
            <div key={item.id} onClick={() => openEdit(item)} style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}>
              {/* Left — icon */}
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(14,154,170,0.1)', border: '2px solid rgba(14,154,170,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>📥</div>

              {/* Middle — details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title || '(No title)'}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
                  {item.company?.name || '—'}{item.contact?.name ? ` · ${item.contact.name}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {item.source && (
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa', fontWeight: 500 }}>
                      {item.source.replace('_',' ')}
                    </span>
                  )}
                  {item.est_value_pence > 0 && (
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: T.tealDim, border: `1px solid ${T.tealGlow}`, color: T.teal, fontWeight: 500 }}>
                      £{(item.est_value_pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 0 })}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: T.textDim, fontFamily: "'DM Mono', monospace" }}>
                    {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                {item.notes && (
                  <div style={{ marginTop: 6, fontSize: 11, color: T.textMuted, fontStyle: 'italic', lineHeight: 1.4 }}>{item.notes}</div>
                )}
              </div>

              {/* Right — actions */}
              {canPromote && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                  <button onClick={e => { e.stopPropagation(); promote(item); }} disabled={promoting === item.id}
                    style={{ padding: '6px 14px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, color: '#10b981', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    {promoting === item.id ? 'Promoting…' : '↑ Promote to New'}
                  </button>
                  <button onClick={e => { e.stopPropagation(); discard(item.id); }} disabled={discarding === item.id}
                    style={{ padding: '6px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#f87171', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {discarding === item.id ? 'Discarding…' : 'Discard'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



export default InboxView;
